/**
 * CreateFromTemplateModal - Clone a system role template into a custom role
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import roleService, { Role } from '../../services/api/roleService';

interface CreateFromTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  systemRoles: Role[];
}

export const CreateFromTemplateModal = ({
  isOpen,
  onClose,
  systemRoles,
}: CreateFromTemplateModalProps) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: () =>
      roleService.createFromTemplate({
        basedOnId: selectedTemplateId,
        name,
        description: description || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      handleClose();
    },
  });

  const handleClose = () => {
    setSelectedTemplateId('');
    setName('');
    setDescription('');
    onClose();
  };

  const selectedTemplate = systemRoles.find((r) => r.id === selectedTemplateId);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create Role from Template">
      <FormContainer>
        <Field>
          <Label>Base Template</Label>
          <Select
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
          >
            <option value="">Select a system role template...</option>
            {systemRoles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name} ({role.permissions?.length || 0} permissions)
              </option>
            ))}
          </Select>
          {selectedTemplate?.description && (
            <HelperText>{selectedTemplate.description}</HelperText>
          )}
        </Field>

        <Field>
          <Label>Role Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Senior BCBA"
            fullWidth
          />
        </Field>

        <Field>
          <Label>Description (optional)</Label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe this role's purpose..."
            fullWidth
          />
        </Field>

        <ButtonRow>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary-blue"
            onClick={() => createMutation.mutate()}
            disabled={!selectedTemplateId || !name.trim() || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create & Configure'}
          </Button>
        </ButtonRow>

        {createMutation.isError && (
          <ErrorText>
            {(createMutation.error as any)?.response?.data?.message || 'Failed to create role'}
          </ErrorText>
        )}
      </FormContainer>
    </Modal>
  );
};

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[4]};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Select = styled.select`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  border: 1px solid ${({ theme }) => theme.colors.borders.default};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: white;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }
`;

const HelperText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const ErrorText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
`;

export default CreateFromTemplateModal;
